// CategoriesSection.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";
import category_img from "../../Assets/category-default.png";
import "./CategoriesSection.css";

const CategoriesSection = ({ categories, onCategoryClick }) => {
  const navigate = useNavigate();

  // Fallback if no categories
  if (!categories || categories.length === 0) {
    return (
      <div className="home-categories-section">
        <div className="home-categories-container">
          <div className="home-categories-header">
            <div className="home-categories-badge">
              <span className="home-categories-badge-icon">✦</span>
              <span>Curated Collections</span>
            </div>
          </div>
          <p style={{ textAlign: 'center', color: '#64748b', padding: '40px 0' }}>
            No categories available
          </p>
        </div>
      </div>
    );
  }

  // Get the base URL for images
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
  
  // Function to get image URL
  const getImageUrl = (image) => {
    if (!image) return category_img;
    // If image is already a full URL or noimages.jpg, use it directly
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    // If image is noimages.jpg or relative path
    if (image.includes('noimages.jpg')) {
      return image; // The path should already be set in the API
    }
    // Otherwise, prepend the base URL
    return `${API_BASE_URL}uploads/categories/${image}`;
  };

  // Handle category click - Navigate to Shop with category filter
  const handleCategoryClick = (category) => {
    // If there's an external onCategoryClick prop, call it first
    if (onCategoryClick) {
      onCategoryClick(category);
    }
    // Navigate to shop page with category parameter
    navigate(`/shop?category=${category.category_id}`);
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Log to see all categories (including inactive ones)
  console.log("All categories loaded:", categories);
  console.log("Total categories count:", categories.length);

  return (
    <div className="home-categories-section">
      {/* Animated background elements */}
      <div className="home-categories-bg-glow"></div>
      <div className="home-categories-bg-grid"></div>

      <div className="home-categories-container">
        <div className="home-categories-header">
          <div className="home-categories-badge">
            <span className="home-categories-badge-icon">✦</span>
            <span>Curated Collections</span>
          </div>
        </div>

        {/* Auto-sliding Swiper component */}
        <div className="home-categories-swiper-wrapper">
          <Swiper
            slidesPerView="auto"
            spaceBetween={32}
            loop={true}
            autoplay={{ delay: 2000, disableOnInteraction: false }}
            modules={[Autoplay]}
            className="home-categories-swiper"
          >
            {categories.map((cat, idx) => (
              <SwiperSlide key={cat.category_id || cat.id || idx} className="home-categories-slide">
                <div
                  className="home-category-card"
                  onClick={() => handleCategoryClick(cat)}
                  style={{ transitionDelay: `${idx * 50}ms` }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleCategoryClick(cat);
                    }
                  }}
                  aria-label={`Browse ${cat.category_name} products`}
                >
                  <div className="home-category-card-inner">
                    <div className="home-category-image">
                      <img
                        src={getImageUrl(cat.image)}
                        alt={cat.category_name}
                        loading="lazy"
                        onError={(e) => {
                          // If image fails to load, use default
                          e.target.src = category_img;
                        }}
                      />
                      <div className="home-category-image-overlay"></div>
                      <div className="home-category-shine"></div>
                      {/* Category name overlay on image */}
                      <div className="home-category-name-overlay">
                        <div className="home-category-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 7L4 7" strokeLinecap="round"/>
                            <path d="M20 12L8 12" strokeLinecap="round"/>
                            <path d="M20 17L4 17" strokeLinecap="round"/>
                            <circle cx="7" cy="7" r="2"/>
                            <circle cx="11" cy="12" r="2"/>
                            <circle cx="7" cy="17" r="2"/>
                          </svg>
                        </div>
                        <h5 className="home-category-name">
                          {cat.category_name}
                          {cat.active === "0" && " (Inactive)"} {/* Optional: Show if inactive */}
                        </h5>
                      </div>
                    </div>
                    <div className="home-category-hover-line"></div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

export default CategoriesSection;